"use client";

import posthog from "posthog-js";
import { useEffect, useState } from "react";

export type FlagValue = string | boolean | undefined;

/**
 * Non-production-only URL override for forcing a flag variant. Lets Cypress
 * specs (and developers / QA in staging) deterministically exercise a
 * variant without depending on posthog-js initialization. Production strips
 * the override at build time via NEXT_PUBLIC_ENVIRONMENT so end users can't
 * self-bucket via the URL bar.
 *
 * Example: `/?ph-flag-<your-flag-key>=test`
 */
export function readDevFlagOverride(key: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  if (process.env.NEXT_PUBLIC_ENVIRONMENT === "production") return undefined;
  const storageKey = `ph-flag-${key}`;
  try {
    const params = new URLSearchParams(window.location.search);
    const urlValue = params.get(storageKey);
    if (urlValue) {
      sessionStorage.setItem(storageKey, urlValue);
      return urlValue;
    }
    return sessionStorage.getItem(storageKey) || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read a PostHog feature flag without blocking on /decide.
 *
 * Returns the cached value immediately (returning visitors with a populated
 * posthog-js localStorage cache get the right value on first paint), and
 * re-renders when posthog-js gets fresh values from /decide or from a
 * post-identify refresh.
 *
 * Use when the flag does NOT gate rendering, e.g. when you tag the variant
 * onto an analytics event, log it, or pick between two equivalent-looking
 * code paths where a brief `undefined` value isn't user-visible.
 *
 * For UI that WOULD flash if the variant flipped after render, use
 * useFlagBlocking instead.
 */
export function useFlag(key: string): FlagValue {
  const override = readDevFlagOverride(key);
  const initial =
    override !== undefined
      ? override
      : typeof window !== "undefined"
        ? posthog.getFeatureFlag(key)
        : undefined;
  const [value, setValue] = useState<FlagValue>(initial);

  useEffect(() => {
    if (readDevFlagOverride(key) !== undefined) return;
    return posthog.onFeatureFlags(() => {
      setValue(posthog.getFeatureFlag(key));
    });
  }, [key]);

  return value;
}

/**
 * Read a PostHog feature flag AND wait for the value to resolve before
 * unlocking the consumer. Returns { variant, isResolved }: render a
 * skeleton / loading state while `!isResolved`, then render the
 * variant-specific UI.
 *
 * Use when a wrong-variant flash would be user-visible (paywall, signup
 * variant, hero copy, anything that changes layout).
 *
 * `maxWaitMs` is a worst-case safety net, NOT the typical wait. In practice
 * the flag resolves in roughly 0.2s because posthog-js fetches /decide on
 * init and our `/decide` rewrite proxies straight to eu.i.posthog.com
 * (see next.config.mjs — wiswiz is on PostHog EU). Returning visitors with
 * a populated localStorage cache resolve synchronously on first paint.
 *
 * If posthog-js does not resolve within maxWaitMs (default 3000ms), bails
 * out with isResolved=true and the cached-or-undefined value, so a slow
 * /decide call can't strand the UI in skeleton-land. The bailout also fires
 * a `feature_flag_wait_timeout` PostHog event so we can monitor how often
 * this happens in the wild; per CLAUDE.md "Analytics is a passive watcher"
 * the capture runs in a microtask so it never blocks the React state
 * update.
 */
export function useFlagBlocking(
  key: string,
  { maxWaitMs = 3000 }: { maxWaitMs?: number } = {}
): { variant: FlagValue; isResolved: boolean } {
  const override = readDevFlagOverride(key);
  const initial =
    override !== undefined
      ? override
      : typeof window !== "undefined"
        ? posthog.getFeatureFlag(key)
        : undefined;
  const [variant, setVariant] = useState<FlagValue>(initial);
  const [isResolved, setIsResolved] = useState(
    override !== undefined || initial !== undefined
  );

  // Always subscribe — posthog-js refreshes flags after identify and on
  // periodic re-evaluation, and we want to pick those up.
  useEffect(() => {
    if (readDevFlagOverride(key) !== undefined) return;
    return posthog.onFeatureFlags(() => {
      setVariant(posthog.getFeatureFlag(key));
      setIsResolved(true);
    });
  }, [key]);

  // One-shot bailout so a slow /decide can't strand the consumer.
  useEffect(() => {
    if (isResolved) return;
    const id = window.setTimeout(() => {
      setIsResolved(true);
      queueMicrotask(() => {
        try {
          posthog.capture("feature_flag_wait_timeout", {
            flag_key: key,
            max_wait_ms: maxWaitMs
          });
        } catch {
          // Capture is observability; never let it raise into React.
        }
      });
    }, maxWaitMs);
    return () => clearTimeout(id);
  }, [maxWaitMs, isResolved, key]);

  return { variant, isResolved };
}
